import { evaluate } from "feelin";
import { Question, QuestionResponseType, Visibility, LocalizedText } from "../types/common";

const toLocalizedText = (primary: string): LocalizedText => ({ primary });

export type CSVRow = {
  status: string;
  name: string;
  labelEN: string;
  labelSV: string;
  optionsEN: string;
  optionsSV: string;
  showIf: string;
  descriptionEN: string;
  descriptionSV: string;
  comment: string;
};

// Joint types to expand #joint# placeholder
const JOINTS = ["knee", "hip", "ankle", "shoulder", "elbow", "hand", "back"];

// Semantic key validation pattern
const SEMANTIC_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Parse a single CSV line handling quoted fields
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let currentField = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      // Field separator
      fields.push(currentField.trim());
      currentField = "";
    } else {
      currentField += char;
    }
  }

  // Push last field
  fields.push(currentField.trim());

  return fields;
}

/**
 * Parse CSV file content into row objects
 */
export function parseCSV(fileContent: string): CSVRow[] {
  const lines = fileContent.split("\n");
  const rows: CSVRow[] = [];

  // Skip header row (index 0)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const fields = parseCSVLine(line);

    rows.push({
      status: fields[0] || "",
      name: fields[1] || "",
      labelEN: fields[2] || "",
      labelSV: fields[3] || "",
      optionsEN: fields[4] || "",
      optionsSV: fields[5] || "",
      showIf: fields[6] || "",
      descriptionEN: fields[7] || "",
      descriptionSV: fields[8] || "",
      comment: fields[9] || "",
    });
  }

  return rows;
}

/**
 * Check if row is a section header (only name column filled)
 */
export function isSectionHeader(row: CSVRow): boolean {
  return row.name.trim() !== "" && row.labelEN.trim() === "" && row.optionsEN.trim() === "";
}

/**
 * Expand #joint# placeholders into 7 joint-specific rows
 */
export function expandJointPlaceholder(row: CSVRow): CSVRow[] {
  if (!row.name.includes("#joint#")) {
    return [row];
  }

  return JOINTS.map(joint => ({
    ...row,
    name: row.name.replace(/#joint#/g, joint),
    labelEN: row.labelEN.replace(/#joint#/g, joint),
    labelSV: row.labelSV.replace(/#joint#/g, joint),
    showIf: row.showIf.replace(/#joint#/g, joint),
    descriptionEN: row.descriptionEN.replace(/#joint#/g, joint),
    descriptionSV: row.descriptionSV.replace(/#joint#/g, joint),
  }));
}

/**
 * Extract content between brackets, handling nested brackets
 */
function extractArrayContent(optionsStr: string): string {
  const startIdx = optionsStr.indexOf("[");
  const endIdx = optionsStr.lastIndexOf("]");
  if (startIdx === -1 || endIdx === -1) return "";
  return optionsStr.substring(startIdx + 1, endIdx);
}

/**
 * Parse multi-select-grouped options with nested arrays
 * Format: [Group1, [Opt1, Opt2], Group2, [Opt3, Opt4]]
 */
function parseMultiSelectGrouped(arrayContent: string): QuestionResponseType {
  const groups: Array<{ label: LocalizedText; variants: LocalizedText[] }> = [];
  let currentGroup: { label: LocalizedText; variants: LocalizedText[] } | null = null;
  let currentToken = "";
  let depth = 0;

  for (let i = 0; i < arrayContent.length; i++) {
    const char = arrayContent[i];

    if (char === "[") {
      depth++;
      if (depth === 1 && currentToken.trim()) {
        // Start of variants for current group
        currentGroup = { label: toLocalizedText(currentToken.trim()), variants: [] };
        currentToken = "";
      }
    } else if (char === "]") {
      depth--;
      if (depth === 0 && currentGroup) {
        // End of variants
        if (currentToken.trim()) {
          currentGroup.variants.push(toLocalizedText(currentToken.trim()));
        }
        groups.push(currentGroup);
        currentGroup = null;
        currentToken = "";
      }
    } else if (char === "," && depth <= 1) {
      // Comma at top level or within group variants
      const token = currentToken.trim();
      if (token) {
        if (depth === 0) {
          // Top-level item (group without variants or standalone)
          if (!currentGroup) {
            // This is a group label without variants (shouldn't happen in proper format)
            // But handle it gracefully
            groups.push({ label: toLocalizedText(token), variants: [] });
          }
        } else {
          // Variant within a group
          if (currentGroup) {
            currentGroup.variants.push(toLocalizedText(token));
          }
        }
      }
      currentToken = "";
    } else {
      currentToken += char;
    }
  }

  // Handle any remaining token
  if (currentToken.trim() && depth === 0) {
    if (currentGroup) {
      groups.push(currentGroup);
    } else {
      groups.push({ label: toLocalizedText(currentToken.trim()), variants: [] });
    }
  }

  return {
    _type: "multi-select-grouped",
    groups,
  };
}

/**
 * Parse options string into appropriate QuestionResponseType
 */
export function parseOptions(optionsStr: string): QuestionResponseType {
  const trimmed = optionsStr.trim();

  if (!trimmed) {
    return { _type: "text", multiline: false };
  }

  // Number input
  if (trimmed === "(number input)") {
    return { _type: "integer", min: 0, max: 1000 };
  }

  // Text input
  if (trimmed === "(text input)") {
    return { _type: "text", multiline: false };
  }

  // Check for (true, false) mapping → checkbox
  if (trimmed.includes("(true, false)")) {
    return { _type: "checkbox" };
  }

  // Parse array format [...]
  if (trimmed.startsWith("[") && trimmed.includes("]")) {
    const arrayContent = extractArrayContent(trimmed);

    // Check if nested (multi-select-grouped)
    if (arrayContent.includes("[")) {
      return parseMultiSelectGrouped(arrayContent);
    }

    // Parse simple array - split by comma but respect quotes
    const options: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < arrayContent.length; i++) {
      const char = arrayContent[i];

      if (char === '"' && (i === 0 || arrayContent[i - 1] !== "\\")) {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        if (current.trim()) {
          // Remove surrounding quotes if present
          const cleaned = current.trim().replace(/^["']|["']$/g, "");
          options.push(cleaned);
        }
        current = "";
      } else {
        current += char;
      }
    }

    // Add last option
    if (current.trim()) {
      const cleaned = current.trim().replace(/^["']|["']$/g, "");
      options.push(cleaned);
    }

    // Simple select with all options as LocalizedText variants
    return {
      _type: "select",
      variants: options.map(toLocalizedText),
    };
  }

  // Fallback
  return { _type: "text", multiline: false };
}

/**
 * Parse showIf to Visibility
 */
export function parseVisibility(showIf: string): Visibility {
  const trimmed = showIf.trim();
  if (!trimmed) {
    return "visible";
  }
  return { visibleWhen: trimmed };
}

// Also export Visibility type for use in other files
export type { Visibility } from "../types/common";

/**
 * Convert CSV row to Question object
 */
export function rowToQuestion(row: CSVRow): Omit<Question, "id" | "hash" | "updatedAt"> {
  const responseType = parseOptions(row.optionsEN);

  const question: Omit<Question, "id" | "hash" | "updatedAt"> = {
    question: toLocalizedText(row.labelEN),
    responseType,
    semanticKey: row.name || undefined,
  };

  // Add description if present
  if (row.descriptionEN.trim()) {
    question.description = toLocalizedText(row.descriptionEN);
  }

  return question;
}

/**
 * Validate semantic key format
 */
export function validateSemanticKey(key: string): { valid: boolean; error?: string } {
  if (!key) {
    return { valid: false, error: "Semantic key is empty" };
  }
  if (!SEMANTIC_KEY_PATTERN.test(key)) {
    return {
      valid: false,
      error: "Must start with letter and contain only alphanumeric characters and underscores",
    };
  }
  return { valid: true };
}

/**
 * Validate FEEL expression syntax
 */
export function validateFeelExpression(expression: string): { valid: boolean; error?: string } {
  if (!expression) {
    return { valid: true }; // Empty is valid (means "visible")
  }

  try {
    // Try to parse the expression with feelin
    evaluate(expression, {});
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : "Invalid FEEL syntax",
    };
  }
}

/**
 * Validate all CSV rows before import
 */
export function validateCSVRows(
  rows: CSVRow[]
): {
  valid: boolean;
  errors: Array<{ row: number; field: string; error: string }>;
} {
  const errors: Array<{ row: number; field: string; error: string }> = [];

  rows.forEach((row, index) => {
    // Skip section headers
    if (isSectionHeader(row)) return;

    // Skip completely empty rows
    if (!row.name && !row.labelEN) return;

    // Validate semantic key (name)
    if (row.name) {
      const expandedRows = expandJointPlaceholder(row);
      expandedRows.forEach(expRow => {
        const keyValidation = validateSemanticKey(expRow.name);
        if (!keyValidation.valid) {
          errors.push({
            row: index + 2, // +2 for header and 1-indexed
            field: "name",
            error: `"${expRow.name}": ${keyValidation.error}`,
          });
        }
      });
    }

    // Validate showIf FEEL expression
    if (row.showIf) {
      const expandedRows = expandJointPlaceholder(row);
      expandedRows.forEach(expRow => {
        const feelValidation = validateFeelExpression(expRow.showIf);
        if (!feelValidation.valid) {
          errors.push({
            row: index + 2,
            field: "showIf",
            error: `"${expRow.showIf}": ${feelValidation.error}`,
          });
        }
      });
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}
